/**
 * Phase HR-JOIN-001 — Employee Joining Workflow detail view.
 * Route: /hr/joining/:id
 *
 * Orchestrator: stepper + 14 tabs + activation modal + photo widget.
 */
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Button, Card, Col, message, Modal, Progress, Row, Space, Spin, Steps, Tabs, Tag, Tooltip,
} from 'antd'
import {
  ArrowLeftOutlined, CheckCircleTwoTone, CloseCircleTwoTone, NumberOutlined, SafetyCertificateOutlined, ThunderboltOutlined,
} from '@ant-design/icons'
import {
  joiningApi,
  type JoiningStatus,
  type ActivationCheckResponse,
} from '@/features/hr/joiningApi'
import { useProvisionalActivate } from '@/features/hr/docGraceApi'
import { usePermission } from '@/shared/hooks/usePermission'
import { displayEmpCode, isDraftEmpCode } from '@/shared/utils/empCode'

import PhotoUploadCard from './joining/PhotoUploadCard'
import BasicDetailsTab from './joining/BasicDetailsTab'
import AddressTab from './joining/AddressTab'
import EmploymentTab from './joining/EmploymentTab'
import AttendanceTab from './joining/AttendanceTab'
import SalaryTab from './joining/SalaryTab'
import StatutoryTab from './joining/StatutoryTab'
import BankTab from './joining/BankTab'
import NomineeTab from './joining/NomineeTab'
import SkillTab from './joining/SkillTab'
import SafetyTab from './joining/SafetyTab'
import DocumentsTab from './joining/DocumentsTab'
import SystemAccessTab from './joining/SystemAccessTab'
import ChecklistTab from './joining/ChecklistTab'
import AuditLogTab from './joining/AuditLogTab'

const STATUS_COLOR: Record<JoiningStatus, string> = {
  DRAFT: 'default',
  DOCUMENT_PENDING: 'gold',
  DOCUMENT_RECEIVED: 'cyan',
  VERIFICATION_PENDING: 'orange',
  VERIFIED: 'lime',
  EMP_CODE_GENERATED: 'blue',
  PAYROLL_PENDING: 'geekblue',
  STATUTORY_PENDING: 'purple',
  BIOMETRIC_PENDING: 'magenta',
  INDUCTION_PENDING: 'volcano',
  APPROVAL_PENDING: 'gold',
  ACTIVE: 'green',
  HOLD: 'default',
  REJECTED: 'red',
}

// 12 lifecycle steps (DRAFT → ACTIVE) — HOLD/REJECTED are off-path
const LIFECYCLE: JoiningStatus[] = [
  'DRAFT',
  'DOCUMENT_PENDING',
  'DOCUMENT_RECEIVED',
  'VERIFICATION_PENDING',
  'VERIFIED',
  'EMP_CODE_GENERATED',
  'PAYROLL_PENDING',
  'STATUTORY_PENDING',
  'BIOMETRIC_PENDING',
  'INDUCTION_PENDING',
  'APPROVAL_PENDING',
  'ACTIVE',
]

export default function JoiningDetailPage() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [activationOpen, setActivationOpen] = useState(false)
  const [checkResult, setCheckResult] = useState<ActivationCheckResponse | null>(null)

  const detailQuery = useQuery({
    queryKey: ['joining-detail', id],
    queryFn: () => joiningApi.detail(id),
    enabled: !!id,
  })

  const checkActivation = useMutation({
    mutationFn: () => joiningApi.activationCheck(id),
    onSuccess: (data) => {
      setCheckResult(data)
      setActivationOpen(true)
    },
  })

  const activate = useMutation({
    mutationFn: () => joiningApi.activate(id),
    onSuccess: (data) => {
      // GOV-001 P5 — activation applies after HR Manager + Plant Head approve.
      if (data.approval_status === 'PENDING_APPROVAL') {
        message.info(data.message ?? 'Activation sent for approval')
      } else {
        message.success(`Employee activated: ${data.emp_code}`)
      }
      setActivationOpen(false)
      qc.invalidateQueries({ queryKey: ['joining-detail', id] })
    },
    onError: (e: unknown) => {
      const err = e as {
        response?: { status?: number; data?: { detail?: unknown } }
      }
      const detail = err.response?.data?.detail
      // A pending request already exists (409) or no matrix rule (422) —
      // the detail is a plain string. Surface it so HR knows the request
      // is already in flight / with an approver, not that checks failed.
      if (typeof detail === 'string') {
        message.warning(detail)
      } else if (detail && typeof detail === 'object' && 'blocked' in detail) {
        message.error('Activation blocked — some mandatory items are pending')
      } else {
        message.error('Activation failed — see check results')
      }
    },
  })

  const generateCode = useMutation({
    mutationFn: () => joiningApi.generateEmpCode(id),
    onSuccess: () => {
      message.success(
        'Marked ready for approval — the employee code is assigned ' +
        'automatically when CHRO approves.',
      )
      qc.invalidateQueries({ queryKey: ['joining-detail', id] })
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err.response?.data?.detail ?? 'Could not advance this record')
    },
  })

  // PJD-001 Task 11 — provisional activation lets an Aadhaar-only labourer
  // start work while the 2nd ID (PAN/Voter/DL/Passport) is chased on the
  // doc-grace ladder. Surfaced only while the SECOND_ID_PROOF checklist
  // item is still open.
  const canProvisional = usePermission('hr.doc_grace.provisional')
  const provisionalActivate = useProvisionalActivate()

  if (detailQuery.isLoading) {
    return <div style={{ padding: 40, textAlign: 'center' }}><Spin size="large" /></div>
  }

  if (!detailQuery.data) {
    return <Card>Joining record not found.</Card>
  }

  const detail = detailQuery.data
  const currentStepIdx = LIFECYCLE.indexOf(detail.joining_status)
  const offPath = currentStepIdx < 0
  const isActive = detail.joining_status === 'ACTIVE'
  const secondIdItem = detail.checklist.find(
    (c) => c.item_code === 'SECOND_ID_PROOF' && c.status === 'PENDING',
  )

  const tabItems = [
    { key: 'basic', label: 'Basic Details', children: <BasicDetailsTab detail={detail} /> },
    { key: 'address', label: 'Address', children: <AddressTab detail={detail} /> },
    { key: 'employment', label: 'Employment', children: <EmploymentTab detail={detail} /> },
    { key: 'attendance', label: 'Attendance', children: <AttendanceTab detail={detail} /> },
    { key: 'salary', label: 'Salary', children: <SalaryTab detail={detail} /> },
    { key: 'statutory', label: 'Statutory', children: <StatutoryTab detail={detail} /> },
    { key: 'bank', label: 'Bank', children: <BankTab detail={detail} /> },
    { key: 'nominee', label: 'Nominee', children: <NomineeTab detail={detail} /> },
    { key: 'skill', label: 'Skill', children: <SkillTab detail={detail} /> },
    { key: 'safety', label: 'Safety', children: <SafetyTab detail={detail} /> },
    { key: 'documents', label: 'Documents', children: <DocumentsTab detail={detail} /> },
    { key: 'system', label: 'System Access', children: <SystemAccessTab detail={detail} /> },
    { key: 'checklist', label: 'Checklist', children: <ChecklistTab detail={detail} /> },
    { key: 'audit', label: 'Audit Log', children: <AuditLogTab detail={detail} /> },
  ]

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 12 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/hr/joining')}
        >
          Back
        </Button>
      </Space>

      {/* Detect legacy backfill workers — bulk-imported, never went through
          the joining form. joining_initiated_at IS NULL is the sentinel. */}
      {(() => null)()}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space direction="vertical" size={4}>
              <Space wrap>
                <h2 style={{ margin: 0 }}>{detail.full_name}</h2>
                <Tag color={STATUS_COLOR[detail.joining_status]}>
                  {detail.joining_status.replace(/_/g, ' ')}
                </Tag>
                {detail.emp_code && (
                  <Tag color={isDraftEmpCode(detail.emp_code) ? 'default' : 'blue'}>
                    {displayEmpCode(detail.emp_code)}
                  </Tag>
                )}
                {isActive && !detail.employee.joining_initiated_at && (
                  <Tag color="purple">LEGACY · backfilling data</Tag>
                )}
              </Space>
              <div style={{ width: 320 }}>
                <Progress
                  percent={detail.completion_percent}
                  size="small"
                  status={isActive ? 'success' : 'active'}
                />
              </div>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button
                icon={<CheckCircleTwoTone />}
                onClick={() => checkActivation.mutate()}
                loading={checkActivation.isPending}
              >
                {isActive ? 'Check Data Completion' : 'Check Activation'}
              </Button>
              {!isActive && detail.joining_status === 'VERIFIED' && (
                <Tooltip title="Employee code and biometric ID are assigned automatically when CHRO gives final approval.">
                  <Button
                    icon={<NumberOutlined />}
                    onClick={() => generateCode.mutate()}
                    loading={generateCode.isPending}
                  >
                    Mark Ready for Approval
                  </Button>
                </Tooltip>
              )}
              {!isActive && (
                <Button
                  type="primary"
                  icon={<ThunderboltOutlined />}
                  onClick={() => activate.mutate()}
                  loading={activate.isPending}
                >
                  Activate Employee
                </Button>
              )}
              {!isActive && canProvisional && secondIdItem && (
                <Tooltip title="Start the worker now on Aadhaar alone — the 2nd ID (PAN/Voter/DL/Passport) is chased on the Document Grace watchlist.">
                  <Button
                    icon={<SafetyCertificateOutlined />}
                    loading={provisionalActivate.isPending}
                    onClick={() =>
                      provisionalActivate.mutate(
                        { employee_id: detail.id, checklist_item_id: secondIdItem.id },
                        {
                          onSuccess: () => {
                            message.success('Provisionally activated — 2nd ID tracked on Document Grace')
                            qc.invalidateQueries({ queryKey: ['joining-detail', id] })
                          },
                          onError: (e: unknown) => {
                            const err = e as { response?: { data?: { detail?: string } } }
                            message.error(err.response?.data?.detail ?? 'Could not provisionally activate')
                          },
                        },
                      )
                    }
                  >
                    Provisional Activate
                  </Button>
                </Tooltip>
              )}
            </Space>
          </Col>
        </Row>

        <div style={{ marginTop: 16 }}>
          <Steps
            current={offPath ? 0 : currentStepIdx}
            size="small"
            status={offPath ? 'error' : isActive ? 'finish' : 'process'}
            items={LIFECYCLE.map((s) => ({
              title: s.replace(/_/g, ' '),
            }))}
          />
          {offPath && (
            <div style={{ marginTop: 8 }}>
              <Tag color="red">Off-path: {detail.joining_status}</Tag>
            </div>
          )}
        </div>
      </Card>

      <Row gutter={16}>
        <Col span={6}>
          <PhotoUploadCard detail={detail} />
        </Col>
        <Col span={18}>
          <Card size="small">
            <Tabs items={tabItems} />
          </Card>
        </Col>
      </Row>

      <Modal
        title={isActive ? 'Data Completion Status' : 'Activation Check Results'}
        open={activationOpen}
        onCancel={() => setActivationOpen(false)}
        width={720}
        footer={
          isActive
            ? [
                <Button key="close" onClick={() => setActivationOpen(false)}>
                  Close
                </Button>,
              ]
            : [
                <Button key="close" onClick={() => setActivationOpen(false)}>
                  Close
                </Button>,
                <Button
                  key="activate"
                  type="primary"
                  disabled={!checkResult?.overall_passed}
                  loading={activate.isPending}
                  onClick={() => activate.mutate()}
                >
                  Activate Now
                </Button>,
              ]
        }
      >
        {checkResult && (
          <div>
            <Space style={{ marginBottom: 12 }}>
              {isActive ? (
                <Tag color={checkResult.overall_passed ? 'green' : 'gold'}>
                  {checkResult.overall_passed ? 'ALL DATA COMPLETE' : `${checkResult.blocked_reasons.length} ITEMS TO COMPLETE`}
                </Tag>
              ) : (
                <Tag color={checkResult.overall_passed ? 'green' : 'red'}>
                  {checkResult.overall_passed ? 'ALL CHECKS PASSED' : 'BLOCKED'}
                </Tag>
              )}
              <span>Completion: {checkResult.completion_percent}%</span>
            </Space>
            {checkResult.blocked_reasons.length > 0 && (
              <Card
                size="small"
                type="inner"
                title={isActive ? 'Pending Data Items' : 'Blocked Reasons'}
                style={{ marginBottom: 12 }}
              >
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {checkResult.blocked_reasons.map((r, i) => (
                    <li key={i} style={{ color: isActive ? '#d48806' : '#cf1322' }}>{r}</li>
                  ))}
                </ul>
              </Card>
            )}
            <div style={{ maxHeight: 380, overflowY: 'auto' }}>
              {checkResult.checks.map((c) => (
                <div
                  key={c.check_code}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '6px 0',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  {c.passed ? (
                    <CheckCircleTwoTone twoToneColor="#52c41a" />
                  ) : (
                    <CloseCircleTwoTone twoToneColor="#f5222d" />
                  )}
                  <div style={{ marginLeft: 8, flex: 1 }}>
                    <div><strong>{c.check_label}</strong></div>
                    {c.detail && (
                      <div style={{ color: '#888', fontSize: 12 }}>{c.detail}</div>
                    )}
                  </div>
                  <code style={{ fontSize: 11, color: '#999' }}>{c.check_code}</code>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
